import { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import type { ActionTemplate, ActionTemplateCreateDTO } from 'config/api/actionLibrary/actionLibrary.types';
import ROLES from 'config/constants/ROLES';
import action_library_messages from 'messages/action_library_messages';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

type UseActionTemplateCopyReturn = {
  copyActionTemplate: (template: ActionTemplate) => Promise<void>;
  loading: boolean;
};

const useActionTemplateCopy = (): UseActionTemplateCopyReturn => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const copyActionTemplate = useCallback(
    async (listTemplate: ActionTemplate) => {
      try {
        setLoading(true);
        // Fetch full details to include steps and other detailed fields
        // We always fetch fresh data to ensure we have the latest version and all fields (steps are missing in list view)
        const { data: template } = await actionTemplatesApi.getSingleActionTemplate(listTemplate.id);

        // @ts-ignore
        const userData = userDataStorage.get() || {};
        const psRoles = (userData as { ps_roles?: string[] }).ps_roles ?? [];
        const isContentEditor = psRoles.includes(ROLES.PS_ROLES.CONTENT_EDITOR);
        const ownerType = isContentEditor ? template.ownerType : 'organization';

        let tags: string[] = [];
        if (Array.isArray(template.tags)) {
          tags = template.tags as string[];
        } else if (template.tags && typeof template.tags === 'object') {
          tags = Object.values(template.tags).map(String);
        }

        // Create a copy by creating new template with same data
        const copyData: ActionTemplateCreateDTO = {
          name: `${template.name} (Copy)`,
          short_description: template.shortDescription,
          purpose: template.purpose,
          area: template.area,
          sub_area: template.subArea || undefined,
          status: 'inactive',
          owner_type: ownerType,
          criticality: template.criticality,
          tags,
          validity_period_months: template.validityPeriodMonths,
          asset_category_ids: template.assetCategories?.map(ac => ac.id),
          reference: template.reference,
          reported_to: template.reportedTo,
          steps: template.steps?.map((step, index) => ({
            title: step.title,
            short_description: step.shortDescription,
            criticality: step.criticality,
            tags: step.tags,
            reference: step.reference,
            reported_to: step.reportedTo,
            relevant_for: step.relevantFor,
            status: 'pending',
            order: step.order ?? index,
            mandatory: step.mandatory,
          })),
        };

        const { data: copiedTemplate } = await actionTemplatesApi.createActionTemplate(copyData);
        enqueueSnackbar(t(action_library_messages.success.copied), { variant: 'success' });

        // If we are already on the detail page of the copied template, we might want to just reload or do nothing
        // But usually we want to navigate to the new template
        history.push(PATHS.ACTION_LIBRARY_DETAIL.replace(':id', copiedTemplate.id.toString()));
      } catch (error) {
        enqueueSnackbar(t(action_library_messages.errors.template_copy_failed), { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [history, enqueueSnackbar, t],
  );

  return { copyActionTemplate, loading };
};

export default useActionTemplateCopy;
